import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Form, Spinner, Table } from "react-bootstrap";
import { FaCloudUploadAlt, FaFileExcel, FaHistory, FaInbox, FaListAlt, FaPaperPlane } from "react-icons/fa";
import api from "../../api";
import "./KpiEvidencePage.css";

// Phase 31. "My KPI Evidence" — the SEPARATE evidence-submission/review
// workflow for FORM/MANUAL-sourced KPIs (Developer Home / Portal
// Performance is finalized and portal-only; this page never renders inside
// it and never re-adds Satisfaction/Sessions/Review Forms UI there). Every
// KPI shown here is whatever the backend's kpi_definitions-driven catalog
// sends — never a hardcoded KPI name/code on this page.
const TABS = [
  { key: "overview", label: "Overview", icon: FaListAlt },
  { key: "form", label: "Form Evidence", icon: FaInbox },
  { key: "manual", label: "Manual / Excel Evidence", icon: FaCloudUploadAlt },
  { key: "submitted", label: "Submitted Evidence", icon: FaPaperPlane },
  { key: "history", label: "Evidence History", icon: FaHistory },
];

function currentMonthValue() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function SourceBadge({ evidenceSource }) {
  if (evidenceSource === "TASK_TYPE") return <Badge bg="primary">Portal</Badge>;
  if (evidenceSource === "FORM") return <Badge bg="info">Form</Badge>;
  if (evidenceSource === "MANUAL") return <Badge bg="warning" text="dark">Manual</Badge>;
  return <Badge bg="secondary">{evidenceSource || "—"}</Badge>;
}

function StatusBadge({ status }) {
  const map = {
    CALCULATED: { bg: "success", label: "Evaluated" },
    MANUAL_PENDING: { bg: "info", label: "Awaiting calculation" },
    INSUFFICIENT_DATA: { bg: "secondary", label: "Not evaluated" },
    DRAFT: { bg: "secondary", label: "Draft" },
    SUBMITTED: { bg: "info", label: "Submitted for review" },
    UNDER_REVIEW: { bg: "info", label: "Under review" },
    APPROVED: { bg: "success", label: "Approved" },
    NEEDS_CHANGES: { bg: "warning", text: "dark", label: "Changes requested" },
    REJECTED: { bg: "danger", label: "Rejected" },
  };
  const cfg = map[status] || { bg: "secondary", label: status || "—" };
  return <Badge bg={cfg.bg} text={cfg.text}>{cfg.label}</Badge>;
}

// Never "No Evidence = 0%" -- always one of these neutral, explained states.
function overviewStatusLabel(kpi) {
  if (kpi.evidence_source === "TASK_TYPE") return "Automatically calculated from Ticketing Portal";
  if (kpi.evidence_source === "FORM") {
    return kpi.monthly?.source_status === "INSUFFICIENT_DATA" ? "Awaiting form responses" : "Collected through Form";
  }
  // MANUAL
  const mine = kpi.my_manual_evidence || [];
  if (!mine.length) return "Evidence required";
  const latest = mine[0];
  if (latest.status === "APPROVED") return "Approved";
  if (latest.status === "NEEDS_CHANGES") return "Changes requested";
  if (latest.status === "REJECTED") return "Rejected — resubmission required";
  if (["SUBMITTED", "UNDER_REVIEW"].includes(latest.status)) return "Submitted for review";
  return "Draft in progress";
}

function FieldInput({ field, value, onChange }) {
  if (field.field_type === "REPEATING_GROUP") {
    const rows = Array.isArray(value) ? value : [];
    const itemSchema = field.repeating_group_item_schema || [];
    const updateRow = (idx, key, v) => {
      const next = rows.map((r, i) => (i === idx ? { ...r, [key]: v } : r));
      onChange(next);
    };
    return (
      <div className="kev-repeating-group">
        <div className="kev-field-label">{field.field_label}{field.is_required ? " *" : ""}</div>
        <div className="unit-table-wrapper">
          <Table size="sm" bordered responsive className="kev-repeating-table">
            <thead>
              <tr>{itemSchema.map((s) => <th key={s.field_key}>{s.field_label}</th>)}<th /></tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx}>
                  {itemSchema.map((s) => (
                    <td key={s.field_key}>
                      {s.field_type === "BOOLEAN" ? (
                        <Form.Check type="checkbox" checked={!!row[s.field_key]} onChange={(e) => updateRow(idx, s.field_key, e.target.checked)} />
                      ) : (
                        <Form.Control
                          size="sm"
                          type={s.field_type === "DATE" ? "date" : "text"}
                          value={row[s.field_key] ?? ""}
                          onChange={(e) => updateRow(idx, s.field_key, e.target.value)}
                        />
                      )}
                    </td>
                  ))}
                  <td>
                    <Button size="sm" variant="outline-danger" onClick={() => onChange(rows.filter((_, i) => i !== idx))}>Remove</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <Button size="sm" variant="outline-primary" onClick={() => onChange([...rows, {}])}>Add Row</Button>
      </div>
    );
  }

  if (field.field_type === "SELECT") {
    return (
      <Form.Group className="mb-2">
        <Form.Label>{field.field_label}{field.is_required ? " *" : ""}</Form.Label>
        <Form.Select value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select…</option>
          {(field.select_options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Form.Select>
      </Form.Group>
    );
  }

  if (field.field_type === "BOOLEAN") {
    return (
      <Form.Group className="mb-2">
        <Form.Check type="checkbox" label={field.field_label} checked={!!value} onChange={(e) => onChange(e.target.checked)} />
      </Form.Group>
    );
  }

  return (
    <Form.Group className="mb-2">
      <Form.Label>{field.field_label}{field.is_required ? " *" : ""}</Form.Label>
      {field.field_type === "TEXTAREA" ? (
        <Form.Control as="textarea" rows={2} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <Form.Control
          type={field.field_type === "DATE" ? "date" : field.field_type === "INTEGER" || field.field_type === "DECIMAL" ? "number" : "text"}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </Form.Group>
  );
}

function ManualEvidenceForm({ kpi, onSubmitted }) {
  const [schema, setSchema] = useState([]);
  const [details, setDetails] = useState({});
  const [periodMonth, setPeriodMonth] = useState(currentMonthValue());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    api.get(`/my-kpi-evidence/kpis/${kpi.kpi_id}/field-schema`)
      .then((res) => setSchema(res?.data?.data || []))
      .catch((err) => setError(err?.response?.data?.message || "Unable to load the evidence form."))
      .finally(() => setLoading(false));
  }, [kpi.kpi_id]);

  const save = async (thenSubmit) => {
    setSaving(true);
    setError("");
    try {
      const created = await api.post("/my-kpi-evidence", { kpi_definition_id: kpi.kpi_id, period_month: periodMonth, details });
      const evidenceId = created?.data?.data?.id;
      if (thenSubmit && evidenceId) {
        await api.post(`/my-kpi-evidence/${evidenceId}/submit`);
      }
      setDetails({});
      onSubmitted?.();
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to save evidence.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="kev-loading"><Spinner animation="border" size="sm" /> Loading form…</div>;

  return (
    <div className="kev-manual-form">
      <Form.Group className="mb-3">
        <Form.Label>Period</Form.Label>
        <Form.Control type="month" value={periodMonth} onChange={(e) => setPeriodMonth(e.target.value)} />
      </Form.Group>
      {schema.map((field) => (
        <FieldInput
          key={field.field_key}
          field={field}
          value={details[field.field_key]}
          onChange={(v) => setDetails((d) => ({ ...d, [field.field_key]: v }))}
        />
      ))}
      {error ? <div className="kev-error">{error}</div> : null}
      <div className="kev-form-actions">
        <Button variant="outline-secondary" disabled={saving} onClick={() => save(false)}>Save as Draft</Button>
        <Button variant="primary" disabled={saving} onClick={() => save(true)}>Submit for Review</Button>
      </div>
    </div>
  );
}

function ExcelImportPanel({ kpi }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kpi_definition_id", kpi.kpi_id);
      const res = await api.post("/kpi-evidence-import/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const data = res?.data?.data;
      setPreview(data);
      setSelectedRows(new Set((data?.rows || []).filter((r) => r.isValid).map((r) => r.rowNumber)));
    } catch (err) {
      setError(err?.response?.data?.message || "Upload failed.");
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!preview?.batchId) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/kpi-evidence-import/${preview.batchId}/commit`, { row_numbers: Array.from(selectedRows) });
      setPreview(null);
      setFile(null);
    } catch (err) {
      setError(err?.response?.data?.message || "Commit failed.");
    } finally {
      setBusy(false);
    }
  };

  const toggleRow = (rowNumber) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowNumber)) next.delete(rowNumber); else next.add(rowNumber);
      return next;
    });
  };

  return (
    <div className="kev-excel-panel">
      <a className="kev-template-link" href={`${api.defaults.baseURL}/kpi-evidence-import/template/${kpi.kpi_id}`} target="_blank" rel="noreferrer">
        <FaFileExcel /> Download Template
      </a>
      <div className="kev-file-row">
        <input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        <Button size="sm" disabled={!file || busy} onClick={upload}>Upload &amp; Preview</Button>
      </div>
      {error ? <div className="kev-error">{error}</div> : null}
      {!preview ? null : !preview.headerValid ? (
        <div className="kev-error">Header row does not match the template. Expected: {(preview.expectedHeaders || []).join(", ")}</div>
      ) : (
        <>
          <div className="kev-import-summary">{preview.validRows} valid / {preview.invalidRows} invalid of {preview.totalRows} row(s)</div>
          <div className="unit-table-wrapper kev-preview-wrap">
            <Table size="sm" bordered responsive>
              <thead><tr><th /><th>Row</th><th>Employee</th><th>Period</th><th>Errors</th></tr></thead>
              <tbody>
                {(preview.rows || []).map((row) => (
                  <tr key={row.rowNumber} className={row.isValid ? "" : "kev-row-invalid"}>
                    <td>
                      <Form.Check type="checkbox" disabled={!row.isValid} checked={selectedRows.has(row.rowNumber)} onChange={() => toggleRow(row.rowNumber)} />
                    </td>
                    <td>{row.rowNumber}</td>
                    <td>{row.email}</td>
                    <td>{row.periodMonth}</td>
                    <td>{row.errors?.join("; ") || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <Button variant="primary" disabled={busy || !selectedRows.size} onClick={commit}>Commit {selectedRows.size} Valid Row(s)</Button>
        </>
      )}
    </div>
  );
}

export default function KpiEvidencePage() {
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedMonth, setSelectedMonth] = useState(currentMonthValue());
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [manualKpis, setManualKpis] = useState([]);
  const [selectedManualKpiId, setSelectedManualKpiId] = useState(null);
  const [manualSubTab, setManualSubTab] = useState("form");
  const [submittedEvidence, setSubmittedEvidence] = useState([]);
  const [historyEvidenceId, setHistoryEvidenceId] = useState(null);
  const [history, setHistory] = useState([]);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/my-kpi-evidence/overview", { params: { month: selectedMonth } });
      setOverview(res?.data?.data || { kras: [] });
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to load KPI evidence overview.");
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  const loadManualKpis = useCallback(async () => {
    try {
      const res = await api.get("/my-kpi-evidence/kpis", { params: { month: selectedMonth } });
      const list = res?.data?.data || [];
      setManualKpis(list);
      if (!selectedManualKpiId && list.length) setSelectedManualKpiId(list[0].kpi_id);
    } catch {
      setManualKpis([]);
    }
  }, [selectedMonth, selectedManualKpiId]);

  const loadSubmitted = useCallback(async () => {
    try {
      const res = await api.get("/my-kpi-evidence");
      setSubmittedEvidence(res?.data?.data || []);
    } catch {
      setSubmittedEvidence([]);
    }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { loadManualKpis(); }, [loadManualKpis]);
  useEffect(() => { if (activeTab === "submitted" || activeTab === "history") loadSubmitted(); }, [activeTab, loadSubmitted]);

  useEffect(() => {
    if (!historyEvidenceId) { setHistory([]); return; }
    api.get(`/my-kpi-evidence/${historyEvidenceId}/history`)
      .then((res) => setHistory(res?.data?.data || []))
      .catch(() => setHistory([]));
  }, [historyEvidenceId]);

  const allKpis = useMemo(() => (overview?.kras || []).flatMap((k) => k.kpis.map((kpi) => ({ ...kpi, kra_name: k.kra_name }))), [overview]);
  const formKpis = useMemo(() => allKpis.filter((k) => k.evidence_source === "FORM"), [allKpis]);
  const selectedManualKpi = manualKpis.find((k) => k.kpi_id === selectedManualKpiId) || null;

  return (
    <div className="unit-page kev-page">
      <div className="kev-hero">
        <div>
          <h2>My KPI Evidence</h2>
          <p>Submit and track evidence for Form and Manual KPIs — separate from Portal Performance, which is calculated automatically.</p>
        </div>
        <Form.Control className="kev-month-picker" type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} />
      </div>

      <div className="kev-tabs" role="tablist">
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" className={activeTab === t.key ? "active" : ""} onClick={() => setActiveTab(t.key)}>
            <t.icon /> {t.label}
          </button>
        ))}
      </div>

      {error ? <div className="kev-error kev-page-error">{error}</div> : null}

      {activeTab === "overview" ? (
        loading ? (
          <div className="kev-loading"><Spinner animation="border" /> Loading…</div>
        ) : (
          <div className="unit-table-wrapper">
            <Table responsive className="unit-table tlp-table">
              <thead>
                <tr>
                  <th>KRA</th><th>KPI</th><th>Source</th><th>Status</th><th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {allKpis.length ? allKpis.map((kpi) => (
                  <tr key={kpi.kpi_id}>
                    <td>{kpi.kra_name}</td>
                    <td>{kpi.kpi_name}</td>
                    <td><SourceBadge evidenceSource={kpi.evidence_source} /></td>
                    <td>{overviewStatusLabel(kpi)}</td>
                    <td className="text-center">
                      {kpi.evidence_source === "MANUAL" ? (
                        <Button size="sm" variant="outline-primary" onClick={() => { setSelectedManualKpiId(kpi.kpi_id); setManualSubTab("form"); setActiveTab("manual"); }}>
                          Submit Evidence
                        </Button>
                      ) : "—"}
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={5} className="text-center kev-empty-row"><FaInbox /> No KRA/KPI configuration assigned.</td></tr>
                )}
              </tbody>
            </Table>
          </div>
        )
      ) : null}

      {activeTab === "form" ? (
        <div className="kev-form-tab">
          {formKpis.length ? formKpis.map((kpi) => (
            <FormEvidenceCard key={kpi.kpi_id} kpi={kpi} month={selectedMonth} />
          )) : <div className="kev-empty-row"><FaInbox /> No Form-evidence KPIs configured.</div>}
        </div>
      ) : null}

      {activeTab === "manual" ? (
        <div className="kev-manual-tab">
          <Form.Group className="mb-3 kev-kpi-select">
            <Form.Label>KPI</Form.Label>
            <Form.Select value={selectedManualKpiId || ""} onChange={(e) => setSelectedManualKpiId(Number(e.target.value))}>
              {manualKpis.map((k) => <option key={k.kpi_id} value={k.kpi_id}>{k.kra_name} — {k.kpi_name}</option>)}
            </Form.Select>
          </Form.Group>
          {selectedManualKpi ? (
            <>
              <div className="kev-sub-tabs">
                <button type="button" className={manualSubTab === "form" ? "active" : ""} onClick={() => setManualSubTab("form")}>Structured Entry</button>
                <button type="button" className={manualSubTab === "excel" ? "active" : ""} onClick={() => setManualSubTab("excel")}>Excel / CSV Import</button>
              </div>
              {manualSubTab === "form" ? (
                <ManualEvidenceForm kpi={selectedManualKpi} onSubmitted={() => { loadOverview(); loadSubmitted(); }} />
              ) : (
                <ExcelImportPanel kpi={selectedManualKpi} />
              )}
            </>
          ) : <div className="kev-empty-row"><FaInbox /> No Manual-evidence KPIs configured.</div>}
        </div>
      ) : null}

      {activeTab === "submitted" ? (
        <div className="unit-table-wrapper">
          <Table responsive className="unit-table tlp-table">
            <thead><tr><th>KRA</th><th>KPI</th><th>Period</th><th>Status</th><th>Updated</th><th className="text-center">Action</th></tr></thead>
            <tbody>
              {submittedEvidence.length ? submittedEvidence.map((e) => (
                <tr key={e.id}>
                  <td>{e.kra_name}</td>
                  <td>{e.kpi_name}</td>
                  <td>{e.period_month}</td>
                  <td><StatusBadge status={e.status} /></td>
                  <td>{new Date(e.updated_at).toLocaleDateString()}</td>
                  <td className="text-center">
                    {e.status === "NEEDS_CHANGES" || e.status === "DRAFT" ? (
                      <Button size="sm" variant="outline-primary" onClick={() => { setSelectedManualKpiId(e.kpi_definition_id); setManualSubTab("form"); setActiveTab("manual"); }}>
                        Edit &amp; Resubmit
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline-secondary" onClick={() => { setHistoryEvidenceId(e.id); setActiveTab("history"); }}>View History</Button>
                    )}
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={6} className="text-center kev-empty-row"><FaInbox /> No submitted evidence yet.</td></tr>
              )}
            </tbody>
          </Table>
        </div>
      ) : null}

      {activeTab === "history" ? (
        <div className="kev-history-tab">
          <Form.Group className="mb-3 kev-kpi-select">
            <Form.Label>Evidence</Form.Label>
            <Form.Select value={historyEvidenceId || ""} onChange={(e) => setHistoryEvidenceId(Number(e.target.value))}>
              <option value="">Select…</option>
              {submittedEvidence.map((e) => <option key={e.id} value={e.id}>{e.kpi_name} — {e.period_month}</option>)}
            </Form.Select>
          </Form.Group>
          {history.length ? (
            <ul className="kev-timeline">
              {history.map((h) => (
                <li key={h.id}>
                  <strong>{h.event_type}</strong>
                  {h.actor_name ? ` — ${h.actor_name}` : ""}
                  <span className="kev-timeline-date">{new Date(h.created_at).toLocaleString()}</span>
                  {h.remarks ? <div className="kev-timeline-remarks">{h.remarks}</div> : null}
                </li>
              ))}
            </ul>
          ) : <div className="kev-empty-row"><FaInbox /> No history to show.</div>}
        </div>
      ) : null}
    </div>
  );
}

function FormEvidenceCard({ kpi, month }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    api.get(`/my-kpi-evidence/form-evidence/${kpi.kpi_id}`, { params: { month } })
      .then((res) => setDetail(res?.data?.data || null))
      .catch(() => setDetail(null));
  }, [kpi.kpi_id, month]);

  return (
    <div className="kev-form-evidence-card">
      <div className="kev-form-evidence-head"><strong>{kpi.kpi_name}</strong><SourceBadge evidenceSource="FORM" /></div>
      {!detail ? (
        <div className="kev-loading"><Spinner animation="border" size="sm" /></div>
      ) : detail.awaiting_responses ? (
        <div className="kev-neutral-state">Awaiting responses</div>
      ) : (
        <div className="kev-form-evidence-body">
          {detail.average_rating != null ? <span>Average Rating: {detail.average_rating} / 5</span> : null}
          {detail.sessions_conducted != null ? <span>Sessions Conducted: {detail.sessions_conducted}</span> : null}
        </div>
      )}
    </div>
  );
}
